/**
 * SocialCopyPreview Component
 *
 * Platform-specific preview cards for LinkedIn, X, and Threads social copies.
 * Shows character counts, platform styling, and edit capabilities.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, Chip, IconButton, Tooltip } from "@mui/material";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const PLATFORM_LIMITS = {
  linkedin: 3000,
  x: 280,
  threads: 500,
  instagram: 2200, // Instagram caption limit
};

const PLATFORM_CONFIG = {
  linkedin: {
    color: "#0A66C2",
    bg: "#EFF6FF",
    icon: "work",
    name: "LinkedIn",
  },
  x: {
    color: "#000000",
    bg: "#F8FAFC",
    icon: "tag",
    name: "X",
  },
  threads: {
    color: "#A855F7",
    bg: "#FDF4FF",
    icon: "forum",
    name: "Threads",
  },
  instagram: {
    color: "#E1306C",
    bg: "#FFF0F6",
    icon: "photo_camera",
    name: "Instagram",
  },
};

function SocialCopyPreview({ linkedinCopy, xCopy, threadsCopy, instagramCopy, onEdit }) {
  const [activeTab, setActiveTab] = useState(0);

  const copies = {
    linkedin: linkedinCopy || "",
    x: xCopy || "",
    threads: threadsCopy || "",
    instagram: instagramCopy || "",
  };

  const platforms = ["linkedin", "x", "threads", "instagram"];
  const currentPlatform = platforms[activeTab];
  const currentCopy = copies[currentPlatform];
  const charCount = currentCopy.length;
  const limit = PLATFORM_LIMITS[currentPlatform] || 2200; // Instagram limit
  const isOverLimit = charCount > limit;
  const config = PLATFORM_CONFIG[currentPlatform] || {
    color: "#E1306C",
    bg: "#FFF0F6",
    icon: "photo_camera",
    name: "Instagram",
  };

  const getCharCountColor = () => {
    const percentage = (charCount / limit) * 100;
    if (percentage > 100) return "#DC2626"; // Red - over limit
    if (percentage > 90) return "#F59E0B"; // Orange - warning
    return "#10B981"; // Green - good
  };

  const handleCopyToClipboard = () => {
    if (currentCopy) {
      navigator.clipboard.writeText(currentCopy);
    }
  };

  return (
    <MDBox>
      {/* Platform Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="fullWidth"
        sx={{
          borderBottom: "1px solid #E5E7EB",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            minHeight: "56px",
            fontSize: "0.875rem",
            color: "#6B7280",
            "&.Mui-selected": {
              color: config.color,
            },
          },
          "& .MuiTabs-indicator": {
            backgroundColor: config.color,
            height: "3px",
          },
        }}
      >
        {platforms.map((platform) => (
          <Tab
            key={platform}
            icon={
              <Icon
                sx={{
                  color:
                    platforms.indexOf(platform) === activeTab
                      ? PLATFORM_CONFIG[platform].color
                      : "#6B7280",
                  fontSize: "1.2rem !important",
                }}
              >
                {PLATFORM_CONFIG[platform].icon}
              </Icon>
            }
            iconPosition="start"
            label={PLATFORM_CONFIG[platform].name}
          />
        ))}
      </Tabs>

      {/* Preview Card */}
      <MDBox sx={{ p: { xs: 2.5, sm: 3 } }}>
        {/* Platform Preview Box */}
        <MDBox
          sx={{
            p: 3,
            borderRadius: "16px",
            backgroundColor: config.bg,
            border: `2px solid ${config.color}20`,
            mb: 3,
            minHeight: "200px",
          }}
        >
          {/* Platform Header */}
          <MDBox display="flex" alignItems="center" mb={2}>
            <MDBox
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: config.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1.5,
              }}
            >
              <Icon sx={{ color: "white", fontSize: "1.2rem" }}>{config.icon}</Icon>
            </MDBox>
            <MDBox>
              <MDTypography variant="button" fontWeight="bold" sx={{ color: config.color }}>
                {config.name} Preview
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#6B7280", display: "block" }}>
                How your post will appear
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* Copy Text */}
          <MDBox
            sx={{
              p: 2,
              backgroundColor: "white",
              borderRadius: "12px",
              minHeight: "120px",
              fontSize: currentPlatform === "x" ? "0.95rem" : "1rem",
              lineHeight: 1.6,
              color: "#1F2937",
              whiteSpace: "pre-wrap",
              fontFamily:
                currentPlatform === "x"
                  ? "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                  : "inherit",
            }}
          >
            {currentCopy || (
              <MDTypography variant="body2" sx={{ color: "#9CA3AF", fontStyle: "italic" }}>
                No {config.name} copy available
              </MDTypography>
            )}
          </MDBox>
        </MDBox>

        {/* Character Count & Actions */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          {/* Character Counter */}
          <MDBox display="flex" alignItems="center" gap={1}>
            <Chip
              label={`${charCount} / ${limit}`}
              size="small"
              sx={{
                backgroundColor: isOverLimit ? "#FEE2E2" : "#F0FDF4",
                color: getCharCountColor(),
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            />
            {isOverLimit && (
              <Chip
                icon={<Icon sx={{ fontSize: "1rem !important" }}>warning</Icon>}
                label="Over limit"
                size="small"
                color="error"
              />
            )}
          </MDBox>

          {/* Action Buttons */}
          <MDBox display="flex" gap={1}>
            <Tooltip title="Copy to clipboard">
              <IconButton
                size="small"
                onClick={handleCopyToClipboard}
                disabled={!currentCopy}
                sx={{
                  backgroundColor: "#F3F4F6",
                  "&:hover": { backgroundColor: "#E5E7EB" },
                  "&:disabled": { backgroundColor: "#F9FAFB", opacity: 0.5 },
                }}
              >
                <Icon>content_copy</Icon>
              </IconButton>
            </Tooltip>
            {onEdit && (
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => onEdit(currentPlatform)}
                sx={{ textTransform: "none" }}
              >
                <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>edit</Icon>
                Edit Copy
              </MDButton>
            )}
          </MDBox>
        </MDBox>

        {/* Platform Tips */}
        <MDBox
          sx={{
            mt: 3,
            p: 2,
            borderRadius: "12px",
            backgroundColor: "#F9FAFB",
            border: "1px solid #E5E7EB",
          }}
        >
          <MDBox display="flex" alignItems="start" gap={1}>
            <Icon sx={{ color: "#6B7280", fontSize: "1.1rem" }}>lightbulb</Icon>
            <MDBox>
              <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#374151" }}>
                {currentPlatform === "linkedin" && "LinkedIn Tips"}
                {currentPlatform === "x" && "X Tips"}
                {currentPlatform === "threads" && "Threads Tips"}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#6B7280", display: "block", mt: 0.5 }}>
                {currentPlatform === "linkedin" &&
                  "Professional tone works best. Add relevant hashtags. Tag companies/people when appropriate."}
                {currentPlatform === "x" &&
                  "Keep it concise and engaging. Emojis perform well. Use 1-2 hashtags max."}
                {currentPlatform === "threads" &&
                  "Authentic and conversational tone. Great for quick insights and visual content."}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

SocialCopyPreview.propTypes = {
  linkedinCopy: PropTypes.string,
  xCopy: PropTypes.string,
  threadsCopy: PropTypes.string,
  instagramCopy: PropTypes.string,
  onEdit: PropTypes.func,
};

SocialCopyPreview.defaultProps = {
  linkedinCopy: "",
  xCopy: "",
  threadsCopy: "",
  instagramCopy: "",
  onEdit: null,
};

export default SocialCopyPreview;
